import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    budget: '',
    timeframe: ''
  });
  
  const [formStep, setFormStep] = useState(1);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formRef, formInView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const [infoRef, infoInView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const [faqRef, faqInView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const nextStep = (e) => {
    e.preventDefault();
    setFormStep(formStep + 1);
  };

  const prevStep = (e) => {
    e.preventDefault();
    setFormStep(formStep - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setFormSubmitted(true);
    }, 1500);
  };

  // Contact info data - Copied from footer contacts
  const contactInfo = {
    phone: "+971 43 868974",
    email: "contact@oasislogic.tech",
    address: "Unit 1004, IT Plaza,Dubai Silicon Oasis, Dubai, UAE",
    hours: "Monday - Friday: 9:00 AM - 6:00 PM EST",
    socials: [
      { icon: "fab fa-linkedin", url: "https://linkedin.com/company/oasislogic" },
      { icon: "fab fa-twitter", url: "https://twitter.com/oasislogictech" },
      { icon: "fab fa-github", url: "https://github.com/oasislogic" },
      { icon: "fab fa-instagram", url: "https://instagram.com/oasislogictech" }
    ]
  };

  // FAQ data
  const faqs = [
    {
      question: "What information should I prepare before our first meeting?",
      answer: "To make our first meeting as productive as possible, prepare a brief description of your project goals, any existing materials or competitors you'd like to reference, your target audience, and your timeline and budget constraints."
    },
    {
      question: "How long does a typical project take to complete?",
      answer: "Project timelines vary greatly depending on scope and complexity. A simple website might take 4-6 weeks, while complex AI integrations or custom software development could take 3-6 months. We'll provide you with a detailed timeline during our initial consultation."
    },
    {
      question: "Do you offer ongoing maintenance and support?",
      answer: "Yes, we offer various maintenance and support packages to keep your solution running optimally. These include regular updates, security monitoring, performance optimization, and technical support with guaranteed response times."
    },
    {
      question: "What is your payment structure?",
      answer: "Our standard payment structure is 30% upfront, 40% at project midpoint, and 30% upon completion. For larger projects, we can establish milestone-based payments. We also offer monthly retainers for ongoing services and maintenance."
    },
    {
      question: "How do you handle project revisions or scope changes?",
      answer: "We include a reasonable number of revisions in our initial proposals. For significant scope changes, we'll discuss the impact on timeline and budget before proceeding. Our agile approach allows for flexibility while ensuring project goals remain achievable."
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  // Form steps rendering
  const renderFormStep = () => {
    if (formSubmitted) {
      return (
        <motion.div 
          className="form-success-message"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="success-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </motion.div>
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Thank You!
          </motion.h3>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            Your message has been sent successfully. We'll get back to you within 24 hours.
          </motion.p>
          <motion.button 
            className="cta-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                service: '',
                message: '',
                budget: '',
                timeframe: ''
              });
              setFormStep(1);
              setFormSubmitted(false);
            }}
          >
            Send Another Message
          </motion.button>
        </motion.div>
      );
    }

    switch(formStep) {
      case 1:
        return (
          <motion.div 
            className="form-step"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.5 }}
          >
            <h3>Let's Get Started</h3>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your full name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone (optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 (123) 456-7890"
              />
            </div>
            <div className="form-group">
              <label htmlFor="company">Company/Organization (optional)</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Your company name"
              />
            </div>
            <div className="form-navigation">
              <motion.button 
                className="cta-button"
                onClick={nextStep}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Next Step
              </motion.button>
            </div>
          </motion.div>
        );
      case 2:
        return (
          <motion.div 
            className="form-step"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5 }}
          >
            <h3>Project Details</h3>
            <div className="form-group">
              <label htmlFor="service">Service Needed</label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a service</option>
                <option value="Web Development">Web Development</option>
                <option value="AI Integration">AI Integration</option>
                <option value="Mobile Development">Mobile Development</option>
                <option value="IT Development & Maintenance">IT Development & Maintenance</option>
                <option value="SaaS Development">SaaS Development</option>
                <option value="Digital Marketing">Digital Marketing</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="budget">Estimated Budget</label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
              >
                <option value="">Select budget range</option>
                <option value="Under $5,000">Under $5,000</option>
                <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                <option value="$50,000+">$50,000+</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="timeframe">Desired Timeframe</label>
              <select
                id="timeframe"
                name="timeframe"
                value={formData.timeframe}
                onChange={handleInputChange}
              >
                <option value="">Select timeframe</option>
                <option value="Less than 1 month">Less than 1 month</option>
                <option value="1-3 months">1-3 months</option>
                <option value="3-6 months">3-6 months</option>
                <option value="6+ months">6+ months</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="message">Project Details</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Please describe your project, goals, and any specific requirements"
                rows="5"
                required
              />
            </div>
            <div className="form-navigation">
              <motion.button 
                className="secondary-button"
                onClick={prevStep}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Back
              </motion.button>
              <motion.button 
                className="cta-button submit-button"
                onClick={handleSubmit}
                disabled={isLoading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isLoading ? (
                  <div className="button-loader">
                    <span className="loader-dot"></span>
                    <span className="loader-dot"></span>
                    <span className="loader-dot"></span>
                  </div>
                ) : "Submit"}
              </motion.button>
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div 
      className="contact-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Contact Hero Section */}
      <motion.section 
        className="services-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-background">
          <div className="neural-network"></div>
          <motion.div
            className="tech-circles"
            animate={{
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="tech-circle"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 3 + i,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: i * 0.3
                }}
                style={{ opacity: 0.05 + (i * 0.01) }}
              />
            ))}
          </motion.div>
        </div>
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="gradient-text"
          >
            Get in Touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            We'd love to hear about your project and how we can help bring your ideas to life
          </motion.p>
        </div>
      </motion.section>

      <div className="contact-main-content">
        {/* Contact Form and Info Grid */}
        <motion.section 
          className="contact-grid-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="contact-grid">
            <motion.div 
              ref={formRef}
              className="contact-form-container glass-morphism"
              variants={containerVariants}
              initial="hidden"
              animate={formInView ? "visible" : "hidden"}
            >
              <motion.h2 variants={itemVariants}>Let's Discuss Your Project</motion.h2>
              <form className="multi-step-form">
                {renderFormStep()}
              </form>
            </motion.div>

            <motion.div 
              ref={infoRef}
              className="contact-info glass-morphism"
              variants={containerVariants}
              initial="hidden"
              animate={infoInView ? "visible" : "hidden"}
            >
              <motion.h2 variants={itemVariants}>Contact Information</motion.h2>
              
              <motion.div className="info-card" variants={itemVariants}>
                <div className="info-icon">
                  <i className="fas fa-phone-alt"></i>
                </div>
                <div className="info-content">
                  <h3>Phone</h3>
                  <p>{contactInfo.phone}</p>
                </div>
              </motion.div>
              
              <motion.div className="info-card" variants={itemVariants}>
                <div className="info-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="info-content">
                  <h3>Email</h3>
                  <p>{contactInfo.email}</p>
                </div>
              </motion.div>
              
              <motion.div className="info-card" variants={itemVariants}>
                <div className="info-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div className="info-content">
                  <h3>Address</h3>
                  <p>{contactInfo.address}</p>
                </div>
              </motion.div>
              
              <motion.div className="info-card" variants={itemVariants}>
                <div className="info-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="info-content">
                  <h3>Business Hours</h3>
                  <p>{contactInfo.hours}</p>
                </div>
              </motion.div>
              
              <motion.div className="social-links" variants={itemVariants}>
                <h3>Connect With Us</h3>
                <div className="social-icons">
                  {contactInfo.socials.map((social, index) => (
                    <motion.a 
                      key={index} 
                      href={social.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      whileHover={{ 
                        scale: 1.2,
                        color: 'var(--primary-color)',
                      }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <i className={social.icon}></i>
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Map Section */}
        <motion.section 
          className="map-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Our Location
          </motion.h2>
          <div className="map-container glass-morphism">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3612.4243667781006!2d55.3959687760527!3d25.12133993476781!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f6474196db7af%3A0x5a027c87ce228cfc!2sIT%20PLAZA%20-%20Dubai%20Silicon%20Oasis%20-%20Dubai!5e0!3m2!1sen!2sae!4v1746755456592!5m2!1sen!2sae"
              width="100%" 
              height="450" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy"
              title="Oasis Logic Office Location"
            ></iframe>
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section 
          ref={faqRef}
          className="faq-section"
          variants={containerVariants}
          initial="hidden"
          animate={faqInView ? "visible" : "hidden"}
        >
          <motion.h2 
            className="section-title"
            variants={itemVariants}
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.div 
            className="faq-container"
            variants={itemVariants}
          >
            {faqs.map((faq, index) => (
              <FaqItem 
                key={index} 
                question={faq.question} 
                answer={faq.answer} 
                index={index}
              />
            ))}
          </motion.div>
        </motion.section>
      </div>
    </motion.div>
  );
};

// FAQ Item Component with animation
const FaqItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div 
      className={`faq-item ${isOpen ? 'active' : ''} glass-morphism`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ 
        boxShadow: isOpen ? 
          '0 10px 30px rgba(0,0,0,0.15), 0 0 20px rgba(56, 189, 248, 0.2)' : 
          '0 5px 15px rgba(0,0,0,0.1)' 
      }}
    >
      <motion.h3 
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ color: 'var(--primary-color)' }}
      >
        {question}
        <motion.span 
          className="faq-icon"
          animate={{ rotate: isOpen ? 270 : 90 }}
          transition={{ duration: 0.3 }}
        >
          ❯
        </motion.span>
      </motion.h3>
      <motion.div 
        className="faq-answer"
        initial="collapsed"
        animate={isOpen ? "expanded" : "collapsed"}
        variants={{
          expanded: { 
            opacity: 1, 
            height: "auto",
            marginTop: "1rem"
          },
          collapsed: { 
            opacity: 0, 
            height: 0,
            marginTop: "0rem"
          }
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {answer}
      </motion.div>
    </motion.div>
  );
};

export default ContactPage;